/**
 * RiderInfoDetail Helpers
 * 라이더 상세정보 페이지 헬퍼 함수 및 상수
 */
import { formatCurrency as sharedFormatCurrency } from '@/shared/utils/formatter';
import { BasicInfoField } from '../types/riderInfoDetail.types';
import {
    ACCOUNT_NUMBER_VALIDATION,
    ACCOUNT_HOLDER_VALIDATION,
    BANK_VALIDATION
} from '../config/riderInfoDetail.config';

// ========================================
// 테이블 상수
// ========================================

export const DEDUCTION_TABLE_COLUMNS = [
    'NO',
    '공제이름',
    '공제금액',
    '시작일자',
    '종료일자'
] as const;

export const DEDUCTION_COLUMN_COUNT = 6;

// ========================================
// 기본정보 필드 정의
// ========================================

export const BASIC_INFO_LEFT_FIELDS: BasicInfoField[] = [
    { label: '협력사명', key: 'supplierName' },
    { label: '이름', key: 'name' },
    { label: '아이디', key: 'id' },
    { label: '계약상태', key: 'contractStatus' },
    { label: '주민등록번호', key: 'residentNumber' },
    { label: '등록일', key: 'registrationDate' },
];

export const BASIC_INFO_RIGHT_FIELDS: BasicInfoField[] = [
    { label: '협력사ID', key: 'supplierId' },
    { label: '휴대폰 번호', key: 'phone' },
    { label: '생년월일', key: 'birthDate' },
    { label: '배달수단', key: 'deliveryMethod' },
    { label: '계약종료일', key: 'contractEndDate' },
    { label: '정산여부', key: 'currentSettlement' },
    { label: '정산변경예정', key: 'nextSettlement' },
];

export const SETTLEMENT_INFO_FIELDS: BasicInfoField[] = [
    { label: '현재 정산 방식', key: 'currentSettlement' },
    { label: '변경 예정 정산 방식', key: 'nextSettlement' },
];

// ========================================
// 포맷팅 함수
// ========================================

/**
 * 금액 포맷팅 (한국 원화)
 */
export const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '-';
    return sharedFormatCurrency(value);
};

/**
 * 값이 없을 때 기본값 반환
 */
export const getDisplayValue = (value: string | null | undefined): string => {
    return value || '-';
};

/**
 * YYYYMMDD 형식을 YYYY-MM-DD로 변환
 */
export const formatSettlementDate = (value: string | null | undefined): string => {
    if (!value || value.length !== 8) return '-';
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
};

// ========================================
// Sanitize 함수
// ========================================

/**
 * 계좌번호 입력값 sanitize (숫자만 허용)
 */
export const sanitizeAccountNumber = (value: string): string => {
    return value.replace(/[^0-9]/g, '');
};

/**
 * 예금주 입력값 sanitize (한글, 영문, 공백만 허용, 최대 길이 제한)
 */
export const sanitizeAccountHolder = (value: string): string => {
    const sanitized = value.replace(/[^가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z\s]/g, '');
    return sanitized.slice(0, ACCOUNT_HOLDER_VALIDATION.MAX_LENGTH);
};

// ========================================
// Validation 함수
// ========================================

/**
 * 계좌 정보 전체 유효성 검사
 */
export const validateAccountInfo = (
    bank: string,
    accountNumber: string,
    accountHolder: string
): string | null => {
    if (!bank || bank.trim() === '') {
        return BANK_VALIDATION.ERROR_MESSAGES.REQUIRED;
    }

    if (!accountNumber || accountNumber.trim() === '') {
        return ACCOUNT_NUMBER_VALIDATION.ERROR_MESSAGES.REQUIRED;
    }

    if (
        accountNumber.length < ACCOUNT_NUMBER_VALIDATION.MIN_LENGTH ||
        accountNumber.length > ACCOUNT_NUMBER_VALIDATION.MAX_LENGTH
    ) {
        return ACCOUNT_NUMBER_VALIDATION.ERROR_MESSAGES.INVALID_LENGTH;
    }

    if (!accountHolder || accountHolder.trim() === '') {
        return ACCOUNT_HOLDER_VALIDATION.ERROR_MESSAGES.REQUIRED;
    }

    if (accountHolder.length < ACCOUNT_HOLDER_VALIDATION.MIN_LENGTH) {
        return ACCOUNT_HOLDER_VALIDATION.ERROR_MESSAGES.INVALID_LENGTH;
    }

    return null;
};
